import { Box, Flex } from '@chakra-ui/react';

import { useMergePurgeStore } from '@feat/reports/context';

import type { ReportGridProps } from './types/report-grid.props';

import AgReportGrid from './ag-report-grid';
import MPSplitsReportGrid from './pseudo-splits-grid';

/**
 * Smart report renderer that selects the correct display component based on `view`.
 *
 * Handles the three layout variants:
 * - `splitinfo` — renders the custom HTML pivot table ({@link MPSplitsReportGrid})
 * - `seedCount` — renders two stacked AG Grid instances (seed count + by mail code)
 * - `full-report` — renders every report in the store stacked vertically at 600 px each
 * - all other views — delegates to a single {@link AgReportGrid}
 *
 * @see {@link ReportGridProps}
 *
 * @example
 * ```tsx
 * <ReportGrid mailingId={123} view="summary" />
 * <ReportGrid mailingId={123} view="full-report" />
 * ```
 */
function ReportGrid({ hasStatusBar = true, mailingId, view }: ReportGridProps) {
  const reports = useMergePurgeStore(({ reports }) => {
    return reports;
  });

  switch (view) {
    case 'full-report':
      return (
        <Flex
          direction="column"
          overflow="auto">
          {reports.map(({ view: reportView }) => {
            switch (reportView) {
              case 'full-report':
                return null;

              case 'seedCount':
                return (
                  <Box
                    h="600px"
                    key={reportView}
                    mb={3}>
                    <Flex
                      direction="column"
                      gap={4}
                      h="full"
                      w="full">
                      <Box className="h-1/2">
                        <AgReportGrid
                          hasStatusBar={false}
                          mailingId={mailingId}
                          view="seedCount"
                        />
                      </Box>
                      <Box className="h-1/2">
                        <AgReportGrid
                          hasStatusBar={false}
                          mailingId={mailingId}
                          view="seedCountByMailCode"
                        />
                      </Box>
                    </Flex>
                  </Box>
                );

              case 'splitinfo':
                return (
                  <Box
                    h="600px"
                    key={reportView}
                    mb={3}>
                    <MPSplitsReportGrid
                      mailingid={mailingId}
                      view={reportView}
                    />
                  </Box>
                );

              default:
                return (
                  <Box
                    h="600px"
                    key={reportView}
                    mb={3}>
                    <AgReportGrid
                      hasStatusBar={false}
                      mailingId={mailingId}
                      view={reportView}
                    />
                  </Box>
                );
            }
          })}
        </Flex>
      );

    case 'seedCount':
      return (
        <Flex
          direction="column"
          gap={4}
          h="full"
          w="full">
          <Box className="h-1/2">
            <AgReportGrid
              hasStatusBar={false}
              key="seedCount"
              mailingId={mailingId}
              view="seedCount"
            />
          </Box>
          <Box className="h-1/2">
            <AgReportGrid
              hasStatusBar={false}
              key="seedCountByMailCode"
              mailingId={mailingId}
              view="seedCountByMailCode"
            />
          </Box>
        </Flex>
      );

    case 'splitinfo':
      return (
        <MPSplitsReportGrid
          key="pseudoSplit"
          mailingid={mailingId}
          view={view}
        />
      );

    default:
      return (
        <AgReportGrid
          hasStatusBar={hasStatusBar}
          key={view}
          mailingId={mailingId}
          view={view}
        />
      );
  }
}

export default ReportGrid;
